import { Accordion, Button, Grid, Icon, Skeleton, Text, useIsDesignLanguage, DesignLanguage, Divider, useIsTheme, Theme } from '@afterpaytouch/core'
import React, { FC, useEffect } from 'react'
import {
  useApplePayForCashCohort,
  useCalculatedPaymentSchedules,
  useCardAddForCashCohort,
  useHideAutopayToggleForCashCohort,
  useIsBillingAgreementApprovalProcess,
  usePaymentPlanChangeHandler,
  useSchedulePreviewText,
  useShowSummaryChangeButton,
  useCohort,
  useIsCheckoutLoadingOrUpdating,
  useIsAfterpayFinanceFeeEligible,
  useExpandedComponentControl,
  useAdjustedDownPaymentMessagingEnabled,
  useDownPaymentContext,
  useCheckoutState,
  useIsCountry,
  useIsDonationEnabled,
} from '../../state'
import styles from './style.module.css'
import clsx from 'clsx'
import { useTranslation } from '../../utils/convergence'
import { ExpandableComponent, PageLayout, TOGGLE_STATES, TrackingEvent } from '../../model'
import {
  PaymentScheduleTitle,
  PaymentMethod,
  OrderBreakdown,
  AutopayMessage,
  AutopayButton,
  EstimatedOrderBreakdown,
  SectionHeader,
  PreferredPaymentDayInline,
  Donation,
} from '..'
import { useGetBillingApprovalSchedule, usePaymentScheduleInitialOpenStatus } from './hooks'
import { useCheckoutSizes } from '../../utils/responsive'
import { useShowPaymentScheduleToast } from '../PaymentScheduleToast'
import { PreferredCardTopazError } from '../PreferredCardTopazError'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { Cohort, FirstPaymentType, Country } from '@afterpaytouch/portal-api'
import { InlinePaymentMethod } from '../InlinePaymentMethod/InlinePaymentMethod'
import { VerticalPaymentScheduleTimeline } from './VerticalPaymentScheduleTimeline'

interface VerticalPaymentScheduleProps {
  testNameSpace?: string
  disableChangeSchedule?: boolean
}

export const VerticalPaymentSchedule: FC<VerticalPaymentScheduleProps> = ({ testNameSpace, disableChangeSchedule = false }) => {
  const { t } = useTranslation()
  const initialOpenStatus = usePaymentScheduleInitialOpenStatus()
  const { setExpandedState, toggleExpandedState, expandableComponentState } = useExpandedComponentControl(ExpandableComponent.VERTICAL_PAYMENT_SCHEDULE)
  const schedules = useCalculatedPaymentSchedules()
  const { text: previewText, isPPDLoading } = useSchedulePreviewText()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const showSummaryChangeButton = useShowSummaryChangeButton()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isKelpieTheme = useIsTheme(Theme.Kelpie)
  const isKelpieMintTheme = useIsTheme(Theme.KelpieMint)
  const useScheduleTitleHeader = isKelpieTheme || isKelpieMintTheme
  const isLoadingOrUpdating = useIsCheckoutLoadingOrUpdating()
  const previewTextSkeletonClassName = clsx(styles.skeletonContainer, styles[`size${textSize}`])
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const adjustedDownPaymentMessagingEnabled = useAdjustedDownPaymentMessagingEnabled()
  const downPaymentContext = useDownPaymentContext()
  const { firstPaymentType, started } = useCheckoutState()
  const isVpufOrNpufPaymentType = firstPaymentType === FirstPaymentType.VariablePaymentUpfront || firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const isUS = useIsCountry(Country.US)
  const isDonationEnabled = useIsDonationEnabled()
  const isGbCountry = useIsCountry(Country.GB)

  const isExpanded = expandableComponentState === 'Expanded' || initialOpenStatus
  const containerClass = clsx(!isArcadeDesignLanguage && styles.accordionContainer)

  const onChangePlanClick = usePaymentPlanChangeHandler()
  const cohort = useCohort()
  const hideAutopayToggleForCashCohort = useHideAutopayToggleForCashCohort()
  const isCardAddForCashCohortEnabled = useCardAddForCashCohort()
  const isApplePayForCashCohortEnabled = useApplePayForCashCohort()
  const billingApprovalSchedule = useGetBillingApprovalSchedule()

  const hasCashCohortAdditionalPaymentMethods = isCardAddForCashCohortEnabled || isApplePayForCashCohortEnabled

  const showAutopayMessage =
    isUS && !isAFF && (cohort === Cohort.APOnly || (cohort === Cohort.CashOnly && hasCashCohortAdditionalPaymentMethods && hideAutopayToggleForCashCohort))

  useShowPaymentScheduleToast()

  useEffect(() => {
    if (isArcadeDesignLanguage && isGbCountry && !isBillingAgreementApprovalProcess) {
      setExpandedState('Expanded')
    }
  }, [isArcadeDesignLanguage, isBillingAgreementApprovalProcess, isGbCountry, setExpandedState])

  const handleChange = (isOpen: boolean): void => {
    logEvent(TrackingEvent.TOGGLED_SUMMARY_PAGE_SCHEDULE, {
      toggledValue: isOpen ? TOGGLE_STATES.EXPANDED : TOGGLE_STATES.COLLAPSED,
    })
    toggleExpandedState()
  }

  const renderPreviewText = (): JSX.Element => {
    if (isLoadingOrUpdating || isPPDLoading) {
      return (
        <div className={previewTextSkeletonClassName}>
          <Skeleton testNameSpace={`${testNameSpace}-skeleton`} />
        </div>
      )
    }

    return (
      <Text size={smallTextSize} color='Subtle' testNameSpace={`${testNameSpace}-preview`}>
        {previewText}
      </Text>
    )
  }

  const renderAccordionButtonContent = (): JSX.Element => {
    if (isArcadeDesignLanguage) {
      return (
        <div className={styles.inlinePaymentScheduleContainer} data-testid='inline-payment-schedule-container'>
          <Icon name='PayInFour' />
          <div className={styles.inlinePaymentScheduleInfoContainer}>
            <Text size={textSize} bold testNameSpace='inline-payment-schedule-title'>
              {isBillingAgreementApprovalProcess
                ? t('summary:billingApprovalSchedule.header', { count: billingApprovalSchedule?.installmentCount })
                : useScheduleTitleHeader
                ? t('summary:scheduleTitle')
                : t('summary:installmentCountPayments', { count: schedules.length })}
            </Text>
            {renderPreviewText()}
            {!isBillingAgreementApprovalProcess && !isVpufOrNpufPaymentType && adjustedDownPaymentMessagingEnabled && (
              <div className={styles.adjustedDownPaymentMessagingContainer}>
                <Text color='Subtle' testNameSpace='adjusted-down-payment'>
                  {t('common:amountDueToday', { context: downPaymentContext, count: schedules.length - 1 })}
                </Text>
                <TrackOnMount
                  eventName={TrackingEvent.VIEWED_ADJUSTED_DOWN_PAYMENT_MESSAGING}
                  eventProperties={{ riskLevel: started?.downPaymentRiskInfo?.downPaymentRiskLevel }}
                />
              </div>
            )}
          </div>
        </div>
      )
    }

    return (
      <>
        <Grid>
          <PaymentScheduleTitle bottomMargin={false} />
          <Grid.FixedItem align='right'>
            <Text size={textSize} testNameSpace={`${testNameSpace}-payment-type-tagline`}>
              {isBillingAgreementApprovalProcess
                ? t('summary:billingApprovalSchedule.header', { count: billingApprovalSchedule?.installmentCount })
                : t('summary:paymentTypeTagline', { installmentCount: schedules.length })}
            </Text>
          </Grid.FixedItem>
        </Grid>
        {renderPreviewText()}
      </>
    )
  }

  return (
    <>
      <SectionHeader title={t('summary:payment')} />
      {!isBillingAgreementApprovalProcess && (
        <div className={containerClass}>
          <Accordion onChange={handleChange} expanded={isExpanded} testNameSpace={testNameSpace} id={ExpandableComponent.VERTICAL_PAYMENT_SCHEDULE}>
            <Accordion.Button>{renderAccordionButtonContent()}</Accordion.Button>
            <Accordion.Details>
              <VerticalPaymentScheduleTimeline testNameSpace={testNameSpace} />
              {showSummaryChangeButton && !disableChangeSchedule && isArcadeDesignLanguage && (
                <div className={styles.changePlanButtonContainer}>
                  <Button
                    data-testid='payment-schedule-change-plan-button'
                    kind='Secondary'
                    size='Large'
                    padding='Fluid'
                    testNameSpace={testNameSpace}
                    onClick={onChangePlanClick}
                  >
                    {t('consumerLending:summary.changePlan')}
                  </Button>
                </div>
              )}
              {showAutopayMessage && <AutopayMessage />}
              {cohort === Cohort.CashOnly && !hideAutopayToggleForCashCohort && <AutopayButton />}
            </Accordion.Details>
          </Accordion>
        </div>
      )}

      {!isBillingAgreementApprovalProcess && <PreferredPaymentDayInline />}

      {isArcadeDesignLanguage ? (
        <InlinePaymentMethod />
      ) : (
        <div className={clsx(styles.paymentButtonContainer, isBillingAgreementApprovalProcess && styles.baModifier)}>
          <PaymentMethod negativeMargins={true} layout={PageLayout.Summary} showErrorMessage={false} showFloatingHeader={false} isGALayout={true} />
        </div>
      )}
      {!isBillingAgreementApprovalProcess && (
        <>
          {!isArcadeDesignLanguage && <PreferredCardTopazError />}
          {cohort !== Cohort.CashOnly && <AutopayButton />}
          {isDonationEnabled && <Divider kind='Hairline' />}
          <Donation />
          <SectionHeader title={t('summary:summary')} />
          <div className={styles.amountsContainer}>{isAFF ? <EstimatedOrderBreakdown /> : <OrderBreakdown />}</div>
        </>
      )}
    </>
  )
}
